import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { Subject, of } from "rxjs";
import { environment } from "../environments/environment";
import { StorageService, StorageKeys } from "./storage.service";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";

interface LastViewedCategoryArgs {
  category?: any;
  page?: number;
  limit?: number;
  scrollOffset?: number;
}

interface Category {
  title: string;
  total: number;
}

interface FileNode {
  type: "file" | "folder";
  name: string;
  contents?: FileNode[];
}

interface FileTypeCount {
  filetype: string;
  count: number;
}

interface WareData {
  filetree?: Record<string, any>;
  file_type_counts?: FileTypeCount[];
  structure?: FileNode[];
}

@Injectable({
  providedIn: "root",
})
export class ExchangeService {
  public wareMap: any = {};
  public lastViewedCategoryArgs: LastViewedCategoryArgs;
  public lastViewedElement: any;
  public categories: Array<Category>;
  public featured: Array<any>;
  public cachedWares: any;
  public cachedSearchResults: any;
  public searching: boolean;
  public searchQuery: string;
  public selectedAttributeFilter: string;
  public lastViewedHomeSection: any;
  public wareDetail$: Subject<any> = new Subject();

  public allowedExtensions = {
    install: true,
    preset: true,
    multipreset: true,
    soundset: true,
    studioonemacro: true,
    quantize: true,
    pitchlist: true,
    colorscheme: true,
    keyswitch: true,
    instrument: true,
    multisample: true,
    soundx: true,
    pattern: true,
    macropage: true,
    ioconfig: true,
    mid: true,
    package: true,
    trackpreset: true,
    colorpalette: true,
  };

  public fileSizeLimits: any = {
    install: 100, // in megabytes
    preset: 100,
    multipreset: 100,
    soundset: 5000, // 500mb
    studioonemacro: 100,
    quantize: 100,
    pitchlist: 100,
    preview: 100,
    colorscheme: 100,
    keyswitch: 100,
    instrument: 100,
    multisample: 100,
    soundx: 100,
    trackpreset: 100,
    pattern: 100,
    macropage: 100,
    ioconfig: 100,
    mid: 100,
    package: 100,
    song: 50000,
    colorpalette: 100,
  };

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    public userService: UserService,
    private _helpers: HelperService
  ) {
    this._helpers.userID = this.userService.user ? this.userService.user.id : undefined;
  }

  /** getCategories() Gets exchange categories. */
  public categories$: Subject<any> = new Subject();
  getCategories(fresh?, all = false) {
    if (this.categories) {
      this.categories$.next(this.categories);
    } else {
      const url = `${environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl}exchange/categories${all && !environment.features.usePaeapiForExchange ? "/true" : ""}`;
      this.http
        .get(url, this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription))
        .pipe(
          map((data: any) => {
            data.forEach((item) => {
              if (item.title == "artists") {
                item.display_name = "Featured";
              }
            });
            this.categories$.next(data);
            this.categories = data;
            this.storage.setItem(StorageKeys.EXCHANGECATEGORIES, data);
          })
        )
        .subscribe();
    }
  }

  getWares(limit: number = 25, page: number = 1, category: any = false, creator_id: any = false) {
    if (this.selectedAttributeFilter) {
      return this.getWaresByAttributeName();
    } else {
      if (!creator_id && category != "myitems") {
        if (!this.lastViewedCategoryArgs) this.lastViewedCategoryArgs = {};
        this.lastViewedCategoryArgs.category = category ? category : "all";
        this.lastViewedCategoryArgs.page = page;
        this.lastViewedCategoryArgs.limit = limit;
      }
      let url = "exchange/wares/";
      if (limit) url += limit + "/" + page;
      if (category) url += "/" + category;
      if (creator_id) url += "/" + creator_id;

      return this.http
        .get(
          (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + url,
          this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription)
        )
        .pipe(
          map((data: any) => {
            data.data.forEach((item) => {
              this.wareMap[item.id] = item;
            });
            return data;
          })
        );
    }
  }

  getWaresByAttributeName() {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchangewares-by-attribute/" : environment.apiUrl + "exchange/wares_by_attribute/";
    return this.http.get(url + this.selectedAttributeFilter, this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        data.data.forEach((item) => {
          this.wareMap[item.id] = item;
        });
        return data;
      })
    );
  }

  getUnapprovedWares() {
    if (environment.features.usePaeapiForExchange) {
      return this.http.get(environment.paeApiUrl + "exchange/unapproved-wares", this._helpers.getHttpOptions());
    }
    return this.http.get(environment.apiUrl + "exchange/unapproved_wares", this._helpers.getHttpOptions());
  }

  getPluginImages() {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/plugin-images/" : environment.apiUrl + "exchange/plugin_images/";
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  deletePluginImage(uuid) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/delete-plugin-image/" : environment.apiUrl + "exchange/delete_plugin_image/";
    return this.http.post(url, JSON.stringify({ uuid: uuid }), this._helpers.getHttpOptions());
  }

  togglePublishWare(id, approved) {
    if (environment.features.usePaeapiForExchange) {
      return this.http.post(environment.paeApiUrl + "exchange/toggle-publish-ware/", JSON.stringify({ ware_id: id, approved: approved }), this._helpers.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "exchange/toggle_publish_ware/", JSON.stringify({ ware_id: id, approved: approved }), this._helpers.getHttpOptions());
  }

  denyWare(ware, reason) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/deny-ware/" : environment.apiUrl + "exchange/deny_ware/";
    return this.http.post(url, JSON.stringify({ ware_id: ware.id, creator_id: ware.user_id, reason: reason }), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getPresignedUrl(args) {
    return this.http.post(environment.apiUrl + "amazonS3/presigned_request/", JSON.stringify(args), this._helpers.getHttpOptions());
  }

  createWareStub(args) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/create-stub-ware/" : environment.apiUrl + "exchange/create_stub_ware/";
    return this.http.post(url, JSON.stringify(args), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        this.wareDetail$.next(data.ware);
        return data;
      })
    );
  }

  getFeaturedWares() {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/featured-wares/" : environment.apiUrl + "exchange/featured_wares/";
    return this.http.get(url, this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription));
  }

  getFeaturedCreators() {
    if (this.featured) {
      return of(this.featured);
    } else {
      const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + "exchange/features/";
      return this.http.get(url, this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription)).pipe(
        map((data: any) => {
          data.reverse(); // newest first.
          data.forEach((item) => {
            item.image_url = encodeURI(item.image_url);
            let words = item.description.split(" ");
            let blurb = "";
            words.forEach((word, i) => {
              if (i < 29) blurb += word + " ";
            });
            blurb += "...";
            item.blurb = blurb;
          });
          this.featured = data;
          return data;
        })
      );
    }
  }

  deleteFeaturedCreator(id) {
    if (environment.features.usePaeapiForExchange) {
      const url = environment.paeApiUrl + `exchange/features/${id}`;
      return this.http.delete(url, this._helpers.getHttpOptions());
    } else {
      const url = environment.apiUrl + "exchange/featudelete_feature/";
      return this.http.get(url + id, this._helpers.getHttpOptions());
    }
  }

  addEditFeaturedCreator(args) {
    const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + "exchange/features/";
    if (args.blurb) delete args.blurb;
    return this.http.post(url, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  private processFileTree(tree: Record<string, any>): { structure: FileNode[]; fileTypeCounts: Record<string, number> } {
    const fileTypeCounts: Record<string, number> = {};
    const structure: FileNode[] = [];

    const processNode = (node: any, name: string): FileNode => {
      if (typeof node === "object") {
        const folderNode: FileNode = {
          type: "folder",
          name,
          contents: Object.entries(node).map(([childName, childNode]) => processNode(childNode, childName)),
        };
        return folderNode;
      } else {
        // Count file types
        if (!fileTypeCounts[node]) fileTypeCounts[node] = 0;
        fileTypeCounts[node]++;

        return {
          type: "file",
          name,
        };
      }
    };

    // Process root level
    Object.entries(tree).forEach(([name, node]) => {
      structure.push(processNode(node, name));
    });

    return { structure, fileTypeCounts };
  }

  private convertFileTypeCountsToArray(fileTypeCounts: Record<string, number>): FileTypeCount[] {
    return Object.entries(fileTypeCounts).map(([filetype, count]) => ({
      filetype,
      count,
    }));
  }

  getWare(id) {
    return this.http
      .get(
        (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + "exchange/ware/" + id,
        this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription)
      )
      .pipe(
        map((data: WareData) => {
          if (data.filetree) {
            const { structure, fileTypeCounts } = this.processFileTree(data.filetree);
            data.file_type_counts = this.convertFileTypeCountsToArray(fileTypeCounts);
            data.structure = structure;
          }
          this.wareDetail$.next(data);
          return data;
        })
      );
  }

  getFavorites() {
    const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + "exchange/favorites/";
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  markAsFavorite(ware_id) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/favorite-ware/" : environment.apiUrl + "exchange/favorite_ware/";
    return this.http.get(url + ware_id, this._helpers.getHttpOptions());
  }

  updateWare(data) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/update-ware/" : environment.apiUrl + "exchange/update_ware/";
    return this.http.post(url, JSON.stringify({ ware: data }), this._helpers.getHttpOptions()).pipe(
      map((data: any) => {
        this.wareDetail$.next(data.ware);
        return data;
      })
    );
  }

  submitRating(args) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/submit-rating/" : environment.apiUrl + "exchange/submit_rating/";
    return this.http.post(url, JSON.stringify(args), this._helpers.getHttpOptions());
  }

  downloadWare(ware_id, package_id) {
    const url = environment.features.usePaeapiForExchange
      ? environment.paeApiUrl + `exchange/download-ware/${ware_id}/${package_id}`
      : environment.apiUrl + `exchange/download_ware/${ware_id}/${package_id}`;
    return this.http.get(url, this._helpers.getHttpOptions());
  }

  removeWarePreview(ware_id, package_id) {
    const url = environment.features.usePaeapiForExchange ? environment.paeApiUrl + "exchange/remove-ware-content-package/" : environment.apiUrl + "exchange/remove_ware_content_package/";
    return this.http.post(url, JSON.stringify({ ware_id: ware_id, package_id: package_id }), this._helpers.getHttpOptions());
  }

  getCreator(user_id) {
    const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + `exchange/creator/${user_id}`;
    return this.http.get(url, this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription)).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  search(query, limit = 25, page = 1) {
    const url = (environment.features.usePaeapiForExchange ? environment.paeApiUrl : environment.apiUrl) + `exchange/search/${query}/${limit}/${page}`;
    return this.http.get(url, this._helpers.getHttpOptions(true, undefined, undefined, this.userService.user.active_subscription));
  }
}
